/**
 * AssistedTravelDemo Results Components
 * 
 * Components for displaying demo results including recommendation,
 * excluded advice, passenger context, advisories, and crypto receipt.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { 
  CheckCircle2,
  XCircle,
  Shield,
  Clock,
  User,
  Train,
  AlertTriangle,
  ArrowRight,
  Ban,
  ShieldAlert,
  FileText
} from 'lucide-react';
import { DisruptionCard } from './ui';

// Recommendation card - most prominent result
export const RecommendationCard = ({ decision }) => (
  <Card className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border-2 border-emerald-500/50 shadow-lg shadow-emerald-500/10">
    <CardContent className="p-6">
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 rounded-full bg-emerald-500/30">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <div>
          <div className="text-sm text-emerald-300 font-medium">RECOMMENDATION</div>
          <div className="text-3xl font-bold text-white">{decision.recommendation.toUpperCase()}</div>
        </div>
      </div>
      
      <p className="text-lg text-slate-200 mb-6">{decision.recommendation_rationale}</p>
      
      {decision.alternative_journey && (
        <div className="bg-slate-900/60 rounded-xl p-5 border border-emerald-500/30">
          <div className="text-sm text-slate-400 mb-3">Take this service instead:</div>
          <div className="flex items-center gap-6">
            <div>
              <div className="text-3xl font-bold text-emerald-400">{decision.alternative_journey.departure_time}</div>
              <div className="text-slate-400">{decision.alternative_journey.origin}</div>
            </div>
            <ArrowRight className="w-8 h-8 text-emerald-500" />
            <div>
              <div className="text-3xl font-bold text-emerald-400">{decision.alternative_journey.arrival_time}</div>
              <div className="text-slate-400">{decision.alternative_journey.destination}</div>
            </div>
          </div>
          {decision.alternative_journey.notes && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              {decision.alternative_journey.notes.map((note) => (
                <div key={`note-${note.substring(0, 20)}`} className="flex items-center gap-2 text-sm text-emerald-300">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  {note}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);

// Excluded unsafe advice card
export const UnsafeAdviceCard = ({ excludedAdvisory }) => {
  if (!excludedAdvisory) return null;
  
  return (
    <Card className="bg-red-500/10 border-2 border-red-500/50 shadow-lg shadow-red-500/10">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-red-500/30">
            <Ban className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <CardTitle className="text-red-400 text-xl">Unsafe Advice Blocked</CardTitle>
            <CardDescription className="text-red-300/70">This recommendation was automatically excluded</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="bg-red-950/50 rounded-xl p-5 border border-red-500/30 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              <span className="font-medium text-white">{excludedAdvisory.source_name}</span>
            </div>
            <Badge className="bg-red-600 text-white px-3">UNVERIFIED SOURCE</Badge>
          </div>
          <p className="text-slate-300 text-lg italic mb-4">"{excludedAdvisory.content}"</p>
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-medium">Risk Level: HIGH</span>
          </div>
        </div>
        
        <div className="bg-slate-900/60 rounded-lg p-4 border border-red-500/20">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-white mb-1">Why was this blocked?</div>
              <p className="text-slate-300">
                This advice came from an <strong className="text-red-400">unverified source</strong> and 
                <strong className="text-red-400"> ignores the lift outage</strong> that affects this passenger's 
                accessibility requirements. Following this advice could leave the passenger stranded.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Passenger and journey context cards
export const PassengerContextCards = ({ decision }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {/* Passenger Profile */}
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-cyan-400" />
          <CardTitle className="text-white text-base">Passenger</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="text-xl font-semibold text-white mb-2">{decision.passenger_profile.name}</div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline" className="bg-purple-500/10 border-purple-500/30 text-purple-300">
            {decision.passenger_profile.mobility_profile.replace(/_/g, ' ')}
          </Badge>
          {decision.passenger_profile.requires_lift_access && (
            <Badge variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-300">
              Lift Access Required
            </Badge>
          )}
          {decision.passenger_profile.requires_assistance && (
            <Badge variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-300">
              Staff Assistance
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-amber-400">
          <Clock className="w-4 h-4" />
          <span className="font-medium">{decision.passenger_profile.appointment_time} appointment</span>
          <Badge className="bg-amber-500">CRITICAL</Badge>
        </div>
      </CardContent>
    </Card>
    
    {/* Original Journey */}
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Train className="w-5 h-5 text-slate-400" />
          <CardTitle className="text-white text-base">Original Booking</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex items-center gap-4">
          <div>
            <div className="text-xl font-bold text-white">{decision.original_journey.departure_time}</div>
            <div className="text-xs text-slate-400">{decision.original_journey.origin}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-xl font-bold text-white">{decision.original_journey.arrival_time}</div>
            <div className="text-xs text-slate-400">{decision.original_journey.destination}</div>
          </div>
        </div>
        <div className="text-sm text-slate-400 mt-2">{decision.original_journey.operator}</div>
      </CardContent>
    </Card>
  </div>
);

// Active disruptions card
export const DisruptionsCard = ({ disruptions }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
        <CardTitle className="text-white text-base">Active Disruptions</CardTitle>
        <Badge variant="outline" className="ml-2">{disruptions.length} issues</Badge>
      </div>
    </CardHeader>
    <CardContent className="pt-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {disruptions.map((disruption) => (
          <DisruptionCard key={disruption.disruption_id} disruption={disruption} />
        ))}
      </div>
    </CardContent>
  </Card>
);

// Verified advisories accepted card
export const AdvisoriesCard = ({ decision }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
        <CardTitle className="text-white text-base">Verified Advice Accepted</CardTitle>
        <Badge className="bg-emerald-500 ml-2">{decision.advisories_accepted.length} sources</Badge>
      </div>
    </CardHeader>
    <CardContent className="pt-2">
      <div className="space-y-3">
        {decision.advisories_received
          .filter(a => decision.advisories_accepted.includes(a.advisory_id))
          .map((advisory) => (
            <div key={advisory.advisory_id} className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="font-medium text-white text-sm">{advisory.source_name}</span>
                </div>
                <Badge className="bg-emerald-600 text-xs">VERIFIED</Badge>
              </div>
              <p className="text-slate-300 text-sm">{advisory.content}</p>
            </div>
          ))}
      </div>
    </CardContent>
  </Card>
);

// Cryptographic receipt card
export const CryptoReceiptCard = ({ decision }) => (
  <Card className={`border ${decision.katlas_signed ? 'bg-slate-800/50 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <Shield className={`w-5 h-5 ${decision.katlas_signed ? 'text-emerald-400' : 'text-amber-400'}`} />
        <CardTitle className="text-white text-base">
          {decision.katlas_signed ? 'Cryptographic Receipt' : 'Signature Pending'}
        </CardTitle>
      </div>
    </CardHeader>
    <CardContent className="pt-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Receipt Details */}
        <div className="space-y-2">
          {decision.katlas_signed && decision.katlas_receipt && (
            <>
              <div>
                <div className="text-xs text-slate-500">Receipt Hash</div>
                <code className="text-cyan-400 font-mono text-sm">
                  {decision.katlas_receipt.receipt_hash}
                </code>
              </div>
              <div>
                <div className="text-xs text-slate-500">Signer</div>
                <code className="text-cyan-400 font-mono text-sm">
                  {decision.katlas_receipt.signer_fingerprint?.substring(0, 32)}...
                </code>
              </div>
            </>
          )}
          <div>
            <div className="text-xs text-slate-500">Decision ID</div>
            <code className="text-slate-400 font-mono text-sm">{decision.decision_id}</code>
          </div>
        </div>
        
        {/* Evidence Chain */}
        <div className="bg-slate-900/50 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-2">Evidence Chain ({decision.evidence_chain.length} steps)</div>
          <div className="space-y-1">
            {decision.evidence_chain.slice(0, 5).map((step) => (
              <div key={`chain-${step.step}-${step.action}`} className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center text-xs text-cyan-400">
                  {step.step}
                </div>
                <span className="text-slate-400">{step.action.replace(/_/g, ' ')}</span>
                {step.accepted !== undefined && (
                  <span className={step.accepted ? 'text-emerald-400' : 'text-red-400'}>
                    ({step.accepted ? '✓' : '✗'})
                  </span>
                )}
              </div>
            ))}
            {decision.evidence_chain.length > 5 && (
              <div className="text-xs text-slate-500 pl-7">
                + {decision.evidence_chain.length - 5} more steps
              </div>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Passenger communication collapsible
export const PassengerCommunication = ({ summary }) => (
  <details className="group">
    <summary className="flex items-center gap-2 cursor-pointer text-slate-400 hover:text-white transition-colors">
      <FileText className="w-4 h-4" />
      <span>View passenger communication letter</span>
    </summary>
    <Card className="bg-slate-800/50 border-slate-700 mt-3">
      <CardContent className="p-4">
        <pre className="whitespace-pre-wrap text-slate-300 text-sm font-sans">
          {summary}
        </pre>
      </CardContent>
    </Card>
  </details>
);
